import { css, cx } from "@emotion/css";
import { EmptyStateNoResults, ErrorPanel, IconButton, OverflowMenu, SearchInput, Tooltip } from "@octopusdeploy/design-system-components";
import { ArrowRefreshIcon } from "@octopusdeploy/design-system-icons";
import { borderWidth, fontFamilyGlobal, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { KubernetesPodLiveStatusDetailedResource } from "@octopusdeploy/octopus-server-client";
import { isPodResource } from "@octopusdeploy/octopus-server-client";
import type { Moment } from "moment/moment";
import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import type { LiveStatusObjectDrawerTabProps } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/TabProps";
import { createErrorInfoFromMonitorError } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/createErrorInfoFromMonitorError";
import { flattenResourceHierarchy } from "~/areas/projects/components/Observability/ObjectDrawer/flattenResourceHierarchy";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import TimeFromNowLabel from "~/components/TimeLabels/TimeFromNowLabel";
import useLocalStorage from "~/hooks/useLocalStorage";
import Select from "~/primitiveComponents/form/Select/Select";
import { delay } from "~/utils/delay";
type LogsTabProps = LiveStatusObjectDrawerTabProps & {
    projectId: string;
    environmentId: string;
    tenantId: string | undefined;
};
const styles = {
    header: css({
        display: "flex",
        flexDirection: "row",
        gap: space[16],
        alignItems: "center",
        paddingLeft: space[16],
        paddingRight: space[16],
        backgroundColor: themeTokens.color.background.primary.default,
    }),
    logsTableContainer: css({
        height: "100%",
        overflowY: "auto",
    }),
    logsTable: css({
        width: "100%",
        borderSpacing: 0,
        tableLayout: "auto",
    }),
    td: css({
        padding: `${space[4]} ${space[8]}`,
        font: text.body.regular.medium,
        color: themeTokens.color.text.primary,
        ":first-of-type": {
            paddingLeft: space[16],
        },
        ":last-of-type": {
            paddingRight: space[16],
        },
    }),
    calloutTd: css({
        padding: `${space[8]} ${space[16]}`,
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
    }),
    topCalloutTd: css({
        borderBottom: `${borderWidth["1"]} solid ${themeTokens.color.border.primary}`,
    }),
    bottomCalloutTd: css({
        borderTop: `${borderWidth["1"]} solid ${themeTokens.color.border.primary}`,
    }),
    logTr: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        ":hover": {
            backgroundColor: themeTokens.color.background.tertiary,
        },
    }),
    logTd: css({
        fontFamily: fontFamilyGlobal["system-ui-monospace"],
    }),
    messageTd: (enableWordWrap: boolean) => css({
        verticalAlign: "top",
        whiteSpace: enableWordWrap ? "pre-line" : "nowrap",
        //the tableLayout: auto is forgiving so this forces this cell to expand to fill the available space
        width: "100%",
    }),
    timestampTd: css({
        verticalAlign: "top",
        color: themeTokens.color.text.primary,
        whiteSpace: "nowrap",
    }),
    loadingTd: css({
        height: `calc(20px + (${space[4]} * 2))`,
        ":first-of-type": {
            width: `calc(15 * ${space[16]})`,
        },
    }),
    refreshLink: css({
        cursor: "pointer",
    }),
    footerContainer: css({
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
    }),
};
type Filters = {
    search?: string;
    podId?: string;
    containerName?: string;
};
export const LogsTab = (props: LogsTabProps) => {
    const { detailedResource, projectId, environmentId, tenantId } = props;
    const [filters, setFilters] = useState<Filters>({});
    const [showTimestampsInStorage, setShowTimestampsInStorage] = useLocalStorage<boolean>("Octopus.LiveStatus.Drawer.ShowTimestampsInLogs", false);
    const [showTimestamps, setShowTimestamps] = useState<boolean>(showTimestampsInStorage);
    const [enableWordWrapInStorage, setEnableWordWrapInStorage] = useLocalStorage<boolean>("Octopus.LiveStatus.Drawer.ShowWordWrapInLogs", false);
    const [enableWordWrap, setEnableWordWrap] = useState<boolean>(enableWordWrapInStorage);
    const [showPreviousContainer, setShowPreviousContainer] = useState<boolean>(false);
    const [requestCompletedTime, setRequestCompletedTime] = useState<Moment>(moment());
    const flattenedResourceHierarchy = useMemo(() => (detailedResource ? flattenResourceHierarchy(detailedResource) : []), [detailedResource]);
    const allPodResources: Record<string, KubernetesPodLiveStatusDetailedResource> = useMemo(() => {
        if (!detailedResource) {
            return {};
        }
        return flattenedResourceHierarchy
            .filter((p) => isPodResource(p))
            .reduce<Record<string, KubernetesPodLiveStatusDetailedResource>>((acc, val) => {
            const id = val.DesiredResourceId || val.ResourceId;
            if (!id) {
                throw new Error("The resource does not have a DesiredResourceId or ResourceId");
            }
            acc[id] = val;
            return acc;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        }, {} as Record<string, KubernetesPodLiveStatusDetailedResource>);
    }, [detailedResource, flattenedResourceHierarchy]);
    const { result, isLoading, error, refetch } = useInlineStatusQuery(async (repo, abortSignal) => {
        if (!detailedResource || !filters.containerName || !filters.podId) {
            return null;
        }
        const podName = allPodResources[filters.podId].Name;
        const { SessionId } = await repo.KubernetesLiveStatus.beginContainerLogSession(projectId, environmentId, tenantId, detailedResource.MachineId, filters.podId, podName, filters.containerName, showPreviousContainer);
        //while this is not aborted, continue attempting to load
        while (!abortSignal.aborted) {
            const result = await repo.KubernetesLiveStatus.getContainerLogs(SessionId);
            if (result.IsSessionCompleted) {
                setRequestCompletedTime(moment());
                return result;
            }
            await delay(100, abortSignal);
        }
    }, [allPodResources, detailedResource, environmentId, filters.containerName, filters.podId, projectId, showPreviousContainer, tenantId], "Begin container log session");
    useEffect(() => {
        if (detailedResource) {
            const firstPodResource = flattenedResourceHierarchy.find((r) => isPodResource(r));
            if (!firstPodResource) {
                throw new Error("No pod resource found.");
            }
            setFilters({
                podId: firstPodResource.DesiredResourceId || firstPodResource.ResourceId,
                containerName: firstPodResource.Details.Containers[0],
            });
        }
    }, [detailedResource, flattenedResourceHierarchy]);
    const containerNames = useMemo(() => {
        //if we have no set container or no set pod, return nothing
        if (!filters.containerName || !filters.podId) {
            return [];
        }
        return allPodResources[filters.podId].Details.Containers;
    }, [allPodResources, filters.containerName, filters.podId]);
    const updatePod = (podId: string | undefined) => {
        if (!podId) {
            throw new Error("No podName selected");
        }
        const podResource = allPodResources[podId];
        const firstContainer = podResource.Details.Containers[0];
        setFilters((prev) => ({
            ...prev,
            podId: podId,
            //clear the search field when the container changes
            search: undefined,
            containerName: firstContainer,
        }));
    };
    const updateContainer = (containerName: string | undefined) => {
        if (!containerName) {
            throw new Error("No containerName selected");
        }
        setFilters((prev) => ({
            ...prev,
            //clear the search field when the container changes
            search: undefined,
            containerName: containerName,
        }));
    };
    const toggleShowTimestamps = (enabled: boolean) => {
        setShowTimestamps(enabled);
        setShowTimestampsInStorage(enabled);
    };
    const toggleEnableWordWrap = (enabled: boolean) => {
        setEnableWordWrap(enabled);
        setEnableWordWrapInStorage(enabled);
    };
    const filteredLogs = useMemo(() => {
        if (!filters.search) {
            return result?.Logs;
        }
        const lowerCaseSearch = filters.search.toLocaleLowerCase();
        return result?.Logs.filter((l) => {
            const inMsg = l.Message.toLocaleLowerCase().includes(lowerCaseSearch);
            if (inMsg) {
                return true;
            }
            //if the timestamps are visible, include those values in the filter
            return showTimestamps ? l.Timestamp.toLocaleLowerCase().includes(lowerCaseSearch) : false;
        });
    }, [filters.search, result?.Logs, showTimestamps]);
    const errorInfo = useMemo(() => createErrorInfo(error) || createErrorInfoFromMonitorError(result?.Error), [error, result?.Error]);
    const totalCols = showTimestamps ? 2 : 1;
    return (<>
            <div className={styles.header}>
                <SearchInput fullWidth={true} value={filters.search || ""} onChange={(val) => setFilters((prev) => ({ ...prev, search: val }))} accessibleName={"Search pod logs"} placeholder={"Search"}/>
                <Select label="Pod" value={filters.podId} onChange={(val) => updatePod(val)} items={Object.entries(allPodResources).map(([id, details]) => ({ value: id, text: details.Name }))} allowClear={false}/>
                <Select label="Container" value={filters.containerName} onChange={(val) => updateContainer(val)} items={containerNames.map((x) => ({ value: x, text: x }))} allowClear={false}/>
                <Tooltip content={"Refresh"}>
                    <IconButton customIcon={<ArrowRefreshIcon size={20}/>} onClick={() => refetch()}/>
                </Tooltip>
                <OverflowMenu menuItems={[
            { type: "toggle", label: "Show previous container", isEnabled: showPreviousContainer, setIsEnabled: (enabled) => setShowPreviousContainer(enabled) },
            { type: "toggle", label: "Show timestamps", isEnabled: showTimestamps, setIsEnabled: (enabled) => toggleShowTimestamps(enabled) },
            { type: "toggle", label: "Wrap lines", isEnabled: enableWordWrap, setIsEnabled: (enabled) => toggleEnableWordWrap(enabled) },
        ]}/>
            </div>
            <div className={styles.logsTableContainer}>
                {errorInfo && <ErrorPanel error={errorInfo}/>}
                {!errorInfo && (<table className={styles.logsTable}>
                        <colgroup>
                            {showTimestamps && <col />}
                            <col />
                        </colgroup>
                        <tbody>
                            {isLoading &&
                Array.from({ length: 5 }, (_, i) => (<tr key={`loadingSkeleton-${i}`}>
                                        {showTimestamps && (<td className={cx(styles.td, styles.logTd, styles.loadingTd)}>
                                                <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                            </td>)}
                                        <td className={cx(styles.td, styles.logTd, styles.loadingTd)}>
                                            <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                        </td>
                                    </tr>))}
                            {!isLoading &&
                filteredLogs &&
                (filteredLogs.length > 0 ? (<>
                                        <tr>
                                            <td className={cx(styles.calloutTd, styles.topCalloutTd)} colSpan={totalCols}>
                                                You've reached the oldest available logs.
                                            </td>
                                        </tr>
                                        {filteredLogs.map((l, i) => (<tr key={`${i}-${l.Timestamp}`} className={styles.logTr}>
                                                {showTimestamps && <td className={cx(styles.td, styles.logTd, styles.timestampTd)}>{l.Timestamp}</td>}
                                                <td className={cx(styles.td, styles.logTd, styles.messageTd(enableWordWrap))}>{l.Message}</td>
                                            </tr>))}
                                        <tr>
                                            <td className={cx(styles.calloutTd, styles.bottomCalloutTd)} colSpan={totalCols}>
                                                <div className={styles.footerContainer}>
                                                    <span>You've reached the latest available logs.</span>
                                                    <div>
                                                        <span>
                                                            Last refreshed <TimeFromNowLabel time={requestCompletedTime}/>.
                                                        </span>{" "}
                                                        <a className={styles.refreshLink} onClick={() => refetch()}>
                                                            Refresh to check for updates.
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    </>) : (<tr>
                                        <td className={styles.td} colSpan={totalCols}>
                                            <EmptyStateNoResults />
                                        </td>
                                    </tr>))}
                        </tbody>
                    </table>)}
            </div>
        </>);
};
